import Button from '@mui/material/Button/Button';
import Typography from '@mui/material/Typography/Typography';
import React, { useCallback } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import { BannerType, TreatmentReviewType } from './Interface';
import {
  DELETE_TREATMENT_REVIEW,
  DISCARD_TREATMENT_REVIEW,
  GET_TREATMENT_REVIEW,
  GET_TREATMENT_REVIEW_LIST,
} from './Queries';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import DiscardDialog from './DiscardDialog';
import dayjs from 'op-utils/dayjs';
import { lighten } from '@mui/system';

const renderDraftBanner = (discardCallback: () => void) => {
  return (
    <>
      <div>
        <ErrorOutlineIcon sx={{ color: (theme) => theme.palette.warning.main }} />
      </div>
      <Typography variant="body2" sx={{ flex: 1, color: (theme) => theme.palette.warning.dark }}>
        You have unsubmitted changes, please submit.
      </Typography>
      <Button
        variant="text"
        size="small"
        sx={{ color: (theme) => theme.palette.warning.dark, fontSize: '13px' }}
        onClick={discardCallback}
        data-testid="treatment-review-discard-changes">
        DISCARD DRAFT
      </Button>
    </>
  );
};

const renderUnsubmittedBanner = (submittedAt: string | null, discardCallback: () => void) => {
  return (
    <>
      <div>
        <ErrorOutlineIcon sx={{ color: (theme) => theme.palette.warning.main }} />
      </div>
      <Typography variant="body2" sx={{ flex: 1, color: (theme) => theme.palette.text.primary }}>
        You have unsubmitted changes, please resubmit. Last submitted at: {submittedAt}
      </Typography>
      <Button
        variant="text"
        size="small"
        sx={{ color: (theme) => theme.palette.warning.dark, fontSize: '13px' }}
        onClick={discardCallback}
        data-testid="treatment-review-discard-changes">
        DISCARD CHANGES
      </Button>
    </>
  );
};

const renderSubmittedBanner = () => {
  return (
    <>
      <div>
        <CheckCircleIcon sx={{ color: (theme) => theme.palette.success.main }} />
      </div>
      <Typography variant="body2" sx={{ flex: 1, color: (theme) => theme.palette.success.dark }}>
        This treatment review has been submitted.
      </Typography>
    </>
  );
};

interface BannerProps {
  review: TreatmentReviewType;
  patientId: string;
  discardCallback: () => void;
}

const isChanged = (value: string): boolean => !['', null, []].includes(value);

const getBannerType = (review: TreatmentReviewType): BannerType => {
  if (!review) return BannerType.NONE;
  if (review.status === 'Unsubmitted changes') return BannerType.UNSUBMITTED;
  if (review.status === 'Submitted') return BannerType.SUBMITTED;
  return BannerType.NONE;
};

const Banner = ({ review, patientId, discardCallback }: BannerProps): JSX.Element => {
  const history = useHistory();
  const [openDiscardDialog, setOpenDiscardDialog] = React.useState(false);
  const [deleteTreatmentReview] = useMutation(DELETE_TREATMENT_REVIEW, {
    refetchQueries: [{ query: GET_TREATMENT_REVIEW_LIST, variables: { patientId } }],
  });
  const [discardTreatmentReview] = useMutation(DISCARD_TREATMENT_REVIEW);

  const deleteTreatmentReviewCallback = useCallback(() => {
    deleteTreatmentReview({ variables: { id: review.id } }).then(() => {
      setOpenDiscardDialog(false);
      history.push(`/radiation/patient/${patientId}/treatment-review`);
    });
  }, [deleteTreatmentReview]);
  const discardTreatmentReviewCallback = useCallback(() => {
    discardTreatmentReview({ variables: { groupId: review?.group?.id } }).then(() => {
      setOpenDiscardDialog(false);
      discardCallback();
    });
  }, [discardTreatmentReview, review?.group?.id]);

  const bannerType = getBannerType(review);
  const bannerCallback =
    bannerType === BannerType.DRAFT ? deleteTreatmentReviewCallback : discardTreatmentReviewCallback;
  const bannerRender = () => {
    switch (bannerType) {
      case BannerType.DRAFT:
        return renderDraftBanner(() => {
          setOpenDiscardDialog(true);
        });
      case BannerType.UNSUBMITTED:
        return renderUnsubmittedBanner(dayjs(review.lastSubmittedAt).format('MMM-DD-YYYY hh:mm A'), () => {
          setOpenDiscardDialog(true);
        });
      case BannerType.SUBMITTED:
        return renderSubmittedBanner();
      default:
        return <></>;
    }
  };
  if (bannerType === BannerType.NONE) return <></>;
  if (!review) return <></>;
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '52px',
        backgroundColor: (theme) => {
          return bannerType === BannerType.DRAFT
            ? theme.palette.warning.light
            : bannerType === BannerType.UNSUBMITTED
            ? theme.palette.warning.light
            : bannerType === BannerType.SUBMITTED
            ? lighten(theme.palette.success.light, 0.9)
            : 'inherit';
        },
        alignItems: 'center',
        padding: '8px 8px 8px 16px',
        gap: '12px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12);',
      }}
      data-testid={bannerType === BannerType.DRAFT ? 'draft-changes-banner' : 'unsubmitted-changes-banner'}>
      {bannerRender()}
      <DiscardDialog
        review={review}
        bannerType={bannerType}
        open={openDiscardDialog}
        discardCallback={() => {
          bannerCallback();
        }}
        cancelCallback={() => setOpenDiscardDialog(false)}
      />
    </Box>
  );
};

export default Banner;
